import {observer} from "mobx-react";
import React from "react";
import Chip, {CHIP_COLORS} from "../../../../../components/chip/chip";
import Currency from "../../../../../components/currency/currency";
import {FontBody2} from "../../../../../components/fonts/font-body2/font-body2";
import {FONT_COLOR, FONT_VARIANT} from "../../../../../components/fonts/font-props";
import IconCircleLeft from "../../../../../components/icons/icon-circle-left/icon-circle-left";
import IconCircleRight from "../../../../../components/icons/icon-circle-right/icon-circle-right";
import MediaWithPlaceholder, {MEDIA_VARIANTS} from "../../../../../components/media-with-placeholder/media-with-placeholder";
import {ButtonIcon} from "../../../../../components/inputs/button-icon/button-icon";
import customerCaseStore from "../../stores/customer-case-store";
import IconSwitchToExchangeTransparent from "../../../../../components/icons/icon-switch-to-exchange-transparent/icon-switch-to-exchange-transparent";
import { TOOLTIP_VARIANTS, Tooltip } from "../../../../../components/tooltip/tooltip";
import { TOOLTIP_PLACEMENTS } from "../../../../../components/tooltip/tooltip-box";
import { INCENTIVE_VIRTUAL_CURRENCIES } from "../../../../../models/User";
import Icon from "../../../../../components/icons/icon/icon";
import IconInfo from "../../../../../components/icons/icon-info/icon-info";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { InfoItem } from "../../../../create-rma/components/item-preview";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";

interface IProps{
    customerCaseStore: customerCaseStore;
}

const ProductCaseDrawerHeader: React.FC<IProps> = observer(({customerCaseStore})=>{
    const { getTranslation } = useDashboardTranslation();
    const productCaseStore = customerCaseStore.selectedProductCase!;
    const productStore = productCaseStore.productStore;
    const rmaCardScope = 'dashboard-returns-rma-card';
    
    const validationStatusText = productCaseStore.isValidated ? getTranslation('VALIDATED', rmaCardScope) : getTranslation('ITEMS_PENDING_VALIDATION', rmaCardScope);
    const validationChipColor = productCaseStore.isValidated ? CHIP_COLORS.GREEN : CHIP_COLORS.ORANGE;

    const totalDiscounts = productCaseStore.itemsBalanceStore.totalDiscounts;

    const tooltipContent = 
    <>
        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE} variant={FONT_VARIANT.BOLD}>{getTranslation('APPLIED_DISCOUNTS', 'dashboard-returns-rma-card')}:</FontBody2>
        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}> 
            {getTranslation('DISCOUNT_TOOLTIP_DISCOUNT_TYPE_BXGY', 'dashboard-returns-rma-card')}:&nbsp;
            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE} variant={FONT_VARIANT.BOLD}> 
                <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.itemsBalanceStore.bxgyDiscountAmount}/>
            </FontBody2>
        </FontBody2>
        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}> 
            {getTranslation('OTHER_DISCOUNTS', 'dashboard-returns-rma-card')}:&nbsp;
            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE} variant={FONT_VARIANT.BOLD}>
                <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={(productCaseStore.itemsBalanceStore.basicDiscountAmount ?? 0) + (productCaseStore.itemsBalanceStore.customDiscountAmount ?? 0)}/>
            </FontBody2>
        </FontBody2>
    </>

    const onPrevProductCaseClick = () => {
        rmsMixpanelStore.trackEvent('d&_original_item_more_arrows');
        customerCaseStore.previousProductCase();
    }

    const onNextProductCaseClick = () => {
        rmsMixpanelStore.trackEvent('d&_original_item_more_arrows');
        customerCaseStore.nextProductCase();
    }

    return (
        <div className={'product-case-header'}>
            <div className={'controls'}>
                <div className={'buttons'}>
                    <ButtonIcon
                        Component={IconCircleLeft}
                        onClick={onPrevProductCaseClick}
                        disabled={customerCaseStore.disablePreviousProductCaseButton()}
                    />
                    <ButtonIcon
                        Component={IconCircleRight}
                        onClick={onNextProductCaseClick}
                        disabled={customerCaseStore.disableNextProductCaseButton()}
                    />
                </div>
                <div className={'position-text'}>
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>
                        {customerCaseStore.getProductCasePositionOutOfTotal()}
                    </FontBody2>
                </div>
            </div>
            <div className='image-flex'>
                <MediaWithPlaceholder variant={MEDIA_VARIANTS.SMALL} src={productStore.imageUrl} />
                <div className='image-text'>
                    <div><FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{productStore.name}</FontBody2></div>
                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>{productStore.variantTitle}</FontBody2>
                    <span>
                        <FontBody2 color={FONT_COLOR.PRIMARY2}>{getTranslation('PAID_AMOUNT', rmaCardScope)}: </FontBody2>
                        <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                            
                            <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.paidPrice}/>
                        </FontBody2>
                    </span>
                    {productCaseStore.productStore.sku && (
                        <span>
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>{getTranslation('SKU', rmaCardScope)}: </FontBody2>
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                {productCaseStore.productStore.sku}
                            </FontBody2>
                        </span>
                    )}
                </div>
            </div>
            <div className="item-info-grid">
                <InfoItem label={getTranslation('CATALOG_PRICE', 'dashboard-create-rma')} value={
                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.itemsBalanceStore.catalogPrice?.amount}/>
                        { !!productCaseStore.isPartOfCompareAtPriceDiscount &&
                            <>
                                {' ('}
                                <FontBody2 variant={FONT_VARIANT.LINE_TROUGH}>
                                    <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.itemsBalanceStore.compareAtPriceAmount?.amount}/>
                                </FontBody2>
                                {')'}
                            </>
                        }
                    </FontBody2>
                }/>
                <InfoItem label={getTranslation('DISCOUNTS', 'dashboard-create-rma')} value={
                    <div className="item-info-flex">
                        <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                            <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={totalDiscounts}/>
                        </FontBody2>
                        { totalDiscounts > 0 &&
                            <Tooltip 
                                content={tooltipContent}
                                variant={TOOLTIP_VARIANTS.INFO}
                            >
                                <Icon Component={IconInfo}/>
                            </Tooltip>
                        }
                    </div>
                }/>
                <InfoItem label={getTranslation('PAID_AMOUNT', 'dashboard-create-rma')} value={
                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        <Currency currencyCode={customerCaseStore.paymentCurrencyCode} amount={productCaseStore.paidPrice}/>
                    </FontBody2>
                } />
            </div>
            <div>
                {productCaseStore.showValidationChip() && <Chip color={validationChipColor} label={validationStatusText} /> }
            </div>
            <div className="product-case-drawer-header-chips-container">
                {productCaseStore.isSwitchToExchange && (
                    <Tooltip
                        placement={TOOLTIP_PLACEMENTS.BOTTOM}
                        variant={TOOLTIP_VARIANTS.INFO}
                        content={
                            <>
                                {!!productCaseStore.rmaOfferStore.switchToExchangeIncentiveAmount && (<div>
                                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                        {getTranslation('INCENTIVE_GIVEN', 'dashboard-returns-rma-card')}
                                        {" "}
                                        <Currency
                                            currencyCode={productCaseStore.itemsBalanceStore.originalItemPaidPrice?.currency_code}
                                            amount={productCaseStore.rmaOfferStore.switchToExchangeIncentiveAmount}
                                        />
                                    </FontBody2>
                                </div>)}
                                {!!productCaseStore.rmaOfferStore.switchToExchangeIncentiveVirtualCurrency && (<div>
                                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                        {getTranslation('INCENTIVE_TYPE', 'dashboard-returns-rma-card')}
                                        {" "}
                                        {productCaseStore.rmaOfferStore.switchToExchangeIncentiveVirtualCurrency === INCENTIVE_VIRTUAL_CURRENCIES.STORE_CREDITS && getTranslation('STORE_CREDITS', 'dashboard-returns-rma-card')}
                                        {productCaseStore.rmaOfferStore.switchToExchangeIncentiveVirtualCurrency === INCENTIVE_VIRTUAL_CURRENCIES.GIFT_CARD && getTranslation('GIFT_CARD', 'dashboard-returns-rma-card')}
                                    </FontBody2>
                                </div>)}
                            </>
                        }
                    >
                        <Chip label={getTranslation("SWITCH_TO_EXCHANGE", 'dashboard-settings-store')} color={CHIP_COLORS.BLUE} icon={IconSwitchToExchangeTransparent} />
                    </Tooltip>
                )}
            </div>
        </div>
    );
});
export default ProductCaseDrawerHeader;